import { useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { leadHref, useLeadNav } from "@/lib/leadList";
import { Button } from "./ui";

/**
 * ← / → (and J / K) through the list this lead was opened from, with "n of N" (§7.4, D-32).
 *
 * The keyboard half is the point. Working twenty leads used to be eighty interactions — click, act,
 * Back, find your place, click the next — and the Back step loses the filter and the sort. Here the
 * whole loop is A / R / D / →, and the list context rides in the URL so refresh and Back still work.
 *
 * The keys are deliberately unmodified letters, and that is only safe because of the guard below: a
 * handler that fired while someone was typing a note would turn "dead" into a stage change. Anything
 * with a text cursor in it, and anything with a modifier held, is left alone.
 */
export interface LeadKeyActions {
  onAccept?: () => void;
  onReject?: () => void;
  onDead?: () => void;
  onNote?: () => void;
  onEdit?: () => void;
  onCancel?: () => void;
  /** v2.2 P3.1: H toggles the hot flag. */
  onHot?: () => void;
  /** v2.9 P1.2: L opens the call log's quick-log row. */
  onLog?: () => void;
}

function typing(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    target.isContentEditable ||
    Boolean(target.closest("[role='dialog']"))
  );
}

export function LeadNav({ leadId, actions }: { leadId: string; actions?: LeadKeyActions }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const list = params.get("q");
  const index = Number(params.get("i") ?? 0);
  const nav = useLeadNav(leadId, list, Number.isFinite(index) ? index : 0);

  const go = (to: { id: string; index: number } | null) => {
    if (to) navigate(leadHref(to.id, list, to.index));
  };

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      // Escape still works while typing — it is the cancel key, and cancelling is exactly what someone
      // mid-edit wants it to do.
      if (event.key === "Escape") {
        actions?.onCancel?.();
        return;
      }
      if (typing(event.target)) return;
      switch (event.key) {
        case "ArrowLeft":
        case "k":
        case "K":
          event.preventDefault();
          go(nav.prev);
          break;
        case "ArrowRight":
        case "j":
        case "J":
          event.preventDefault();
          go(nav.next);
          break;
        case "a":
        case "A":
          if (actions?.onAccept) {
            event.preventDefault();
            actions.onAccept();
          }
          break;
        case "r":
        case "R":
          if (actions?.onReject) {
            event.preventDefault();
            actions.onReject();
          }
          break;
        case "d":
        case "D":
          if (actions?.onDead) {
            event.preventDefault();
            actions.onDead();
          }
          break;
        case "n":
        case "N":
          if (actions?.onNote) {
            event.preventDefault();
            actions.onNote();
          }
          break;
        case "h":
        case "H":
          if (actions?.onHot) {
            event.preventDefault();
            actions.onHot();
          }
          break;
        case "l":
        case "L":
          if (actions?.onLog) {
            event.preventDefault();
            actions.onLog();
          }
          break;
        case "e":
        case "E":
          if (actions?.onEdit) {
            event.preventDefault();
            actions.onEdit();
          }
          break;
        default:
          break;
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav.prev?.id, nav.next?.id, list, actions]);

  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid="lead-nav">
      <div className="flex rounded border">
        <Button
          size="sm"
          variant="ghost"
          disabled={!nav.prev}
          onClick={() => go(nav.prev)}
          title="Previous in this list (← or K)"
          data-testid="lead-nav-prev"
        >
          ←
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={!nav.next}
          onClick={() => go(nav.next)}
          title="Next in this list (→ or J)"
          data-testid="lead-nav-next"
        >
          →
        </Button>
      </div>
      <span className="tabular-nums text-fg-muted" data-testid="lead-nav-position">
        {nav.loading ? "…" : `${nav.position} of ${nav.total}`}
      </span>
      <span className="text-fg-muted">in {nav.label}</span>
      {list && (
        <Link
          to={list.startsWith("archived") ? "/archived" : list.startsWith("today") ? "/" : "/leads"}
          className="underline text-fg-muted"
        >
          back to the list
        </Link>
      )}
      <span className="ml-auto hidden text-[11px] text-fg-muted sm:inline">
        A accept · R reject · D dead · N note · H hot · E edit · Esc cancel
      </span>
    </div>
  );
}
